export type HostEnvironment = 'production' | 'staging' | 'preview' | 'local';

export type HostSurface =
  | 'account'
  | 'ops'
  | 'app'
  | 'karrier'
  | 'portal'
  | 'legacy-site'
  | 'root';

export type AuthAudience = 'owner' | 'consumer';
export type HostRequestKind = 'application' | 'legacy-site' | 'root' | 'localhost';
export type PortalUrlMode = 'durable' | 'preview-display';
export type SupportedBaseDomain = 'keyond.ca' | 'keyondapp.ca';

export interface HostContext {
  hostname: string;
  host: string;
  port?: number;
  environment: HostEnvironment;
  baseDomain: SupportedBaseDomain | null;
  cookieDomain: `.${ SupportedBaseDomain }` | null;
  origin: string;
  surface: HostSurface;
  requestKind: HostRequestKind;
  branch?: string;
  portalSlug?: string;
  legacySite?: string;
}

export interface ParseHostOptions {
  deploymentBranch?: string;
  protocol?: 'http' | 'https';
}

export interface HostCookieOptions {
  domain?: string;
  path: string;
  sameSite: 'lax';
  secure: boolean;
}

export type SupabaseCookieOptions = HostCookieOptions & { name: string };

export const SUPABASE_STORAGE_KEYS: Record<AuthAudience, string> = {
  owner: 'keyond-owner-auth-token',
  consumer: 'keyond-consumer-auth-token',
};

export interface CanonicalOriginEnvironment {
  [key: string]: string | undefined;
  KEYOND_CANONICAL_ORIGIN?: string;
  NEXT_PUBLIC_CANONICAL_ORIGIN?: string;
  KEYOND_ENV?: string;
  VERCEL_ENV?: string;
  VERCEL_GIT_COMMIT_REF?: string;
}

const reservedSurfaces = new Set(['account', 'app', 'karrier', 'ops']);
const legacySites = new Set(['avenue', 'demo', 'oceanfrontsquamish']);
const dnsLabelPattern = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function normalizeDeploymentBranch(value: string | undefined): string | undefined {
  if (!value)
    return undefined;

  const candidate = value.trim().toLowerCase().split('/').filter(Boolean).pop();
  return candidate && dnsLabelPattern.test(candidate) ? candidate : undefined;
}

function normalizeHostHeader(value: string | null | undefined): { hostname: string; port?: number; host: string } | null {
  if (typeof value !== 'string')
    return null;

  let candidate = value.trim().toLowerCase();
  if (!candidate || /[\s/?#@]/.test(candidate))
    return null;

  if (candidate.endsWith('.'))
    candidate = candidate.slice(0, -1);

  let port: number | undefined;
  const portSeparator = candidate.lastIndexOf(':');
  if (portSeparator !== -1) {
    const portValue = candidate.slice(portSeparator + 1);
    if (!/^\d{1,5}$/.test(portValue) || candidate.indexOf(':') !== portSeparator)
      return null;

    port = Number(portValue);
    if (port > 65535)
      return null;

    candidate = candidate.slice(0, portSeparator);
  }

  if (!candidate || candidate.includes(':'))
    return null;

  const labels = candidate.split('.');
  if (candidate.length > 253 || labels.some((label) => !dnsLabelPattern.test(label)))
    return null;

  return {
    hostname: candidate,
    ...(port === undefined ? {} : { port }),
    host: port === undefined ? candidate : `${ candidate }:${ port }`,
  };
}

function originFor(host: string, environment: HostEnvironment, protocol?: 'http' | 'https'): string {
  const scheme = environment === 'local' ? protocol ?? 'http' : 'https';
  return `${ scheme }://${ host }`;
}

function contextForSurface(args: {
  normalizedHost: { hostname: string; port?: number; host: string };
  environment: HostEnvironment;
  baseDomain: SupportedBaseDomain | null;
  surface: HostSurface;
  requestKind: HostRequestKind;
  branch?: string;
  portalSlug?: string;
  legacySite?: string;
  protocol?: 'http' | 'https';
}): HostContext {
  const cookieDomain = args.baseDomain ? `.${ args.baseDomain }` as const : null;

  return {
    hostname: args.normalizedHost.hostname,
    host: args.normalizedHost.host,
    ...(args.normalizedHost.port === undefined ? {} : { port: args.normalizedHost.port }),
    environment: args.environment,
    baseDomain: args.baseDomain,
    cookieDomain,
    origin: originFor(args.normalizedHost.host, args.environment, args.protocol),
    surface: args.surface,
    requestKind: args.requestKind,
    ...(args.branch === undefined ? {} : { branch: args.branch }),
    ...(args.portalSlug === undefined ? {} : { portalSlug: args.portalSlug }),
    ...(args.legacySite === undefined ? {} : { legacySite: args.legacySite }),
  };
}

function publicContext(
  normalizedHost: { hostname: string; port?: number; host: string },
  baseDomain: SupportedBaseDomain,
  environment: HostEnvironment,
  labels: string[],
  options: ParseHostOptions,
): HostContext | null {
  const domainLabelCount = baseDomain.split('.').length;
  const prefix = labels.slice(0, -domainLabelCount);

  if (prefix.length === 0)
    return contextForSurface({
      normalizedHost,
      environment,
      baseDomain,
      surface: 'root',
      requestKind: 'root',
      protocol: options.protocol,
    });

  if (prefix.length === 1) {
    const label = prefix[0];
    const deploymentBranch = normalizeDeploymentBranch(options.deploymentBranch);
    if (baseDomain === 'keyondapp.ca' && deploymentBranch === label)
      return null;

    if (label === 'www')
      return contextForSurface({
        normalizedHost,
        environment,
        baseDomain,
        surface: 'root',
        requestKind: 'root',
        protocol: options.protocol,
      });

    if (legacySites.has(label))
      return contextForSurface({
        normalizedHost,
        environment,
        baseDomain,
        surface: 'legacy-site',
        requestKind: 'legacy-site',
        legacySite: label,
        protocol: options.protocol,
      });

    return contextForSurface({
      normalizedHost,
      environment,
      baseDomain,
      surface: reservedSurfaces.has(label) ? label as 'account' | 'app' | 'karrier' | 'ops' : 'portal',
      requestKind: 'application',
      ...(reservedSurfaces.has(label) ? {} : { portalSlug: label }),
      protocol: options.protocol,
    });
  }

  if (prefix.length !== 2 || baseDomain !== 'keyondapp.ca')
    return null;

  const [surfaceLabel, branch] = prefix;
  if (surfaceLabel === 'www')
    return null;

  if (legacySites.has(surfaceLabel))
    return contextForSurface({
      normalizedHost,
      environment: 'preview',
      baseDomain,
      surface: 'legacy-site',
      requestKind: 'legacy-site',
      branch,
      legacySite: surfaceLabel,
      protocol: options.protocol,
    });

  return contextForSurface({
    normalizedHost,
    environment: 'preview',
    baseDomain,
    surface: reservedSurfaces.has(surfaceLabel) ? surfaceLabel as 'account' | 'app' | 'karrier' | 'ops' : 'portal',
    requestKind: 'application',
    branch,
    ...(reservedSurfaces.has(surfaceLabel) ? {} : { portalSlug: surfaceLabel }),
    protocol: options.protocol,
  });
}

export function parseHostContext(
  hostHeader: string | null | undefined,
  options: ParseHostOptions = {},
): HostContext | null {
  const normalizedHost = normalizeHostHeader(hostHeader);
  if (!normalizedHost)
    return null;

  const labels = normalizedHost.hostname.split('.');
  if (normalizedHost.hostname === 'localhost')
    return contextForSurface({
      normalizedHost,
      environment: 'local',
      baseDomain: null,
      surface: 'root',
      requestKind: 'localhost',
      protocol: options.protocol,
    });

  if (labels.length === 2 && labels[1] === 'localhost') {
    const [label] = labels;
    if (legacySites.has(label))
      return contextForSurface({
        normalizedHost,
        environment: 'local',
        baseDomain: null,
        surface: 'legacy-site',
        requestKind: 'legacy-site',
        legacySite: label,
        protocol: options.protocol,
      });

    return contextForSurface({
      normalizedHost,
      environment: 'local',
      baseDomain: null,
      surface: reservedSurfaces.has(label) ? label as 'account' | 'app' | 'karrier' | 'ops' : 'portal',
      requestKind: 'localhost',
      ...(reservedSurfaces.has(label) ? {} : { portalSlug: label }),
      protocol: options.protocol,
    });
  }

  for (const baseDomain of ['keyond.ca', 'keyondapp.ca'] as const) {
    const suffix = `.${ baseDomain }`;
    if (normalizedHost.hostname === baseDomain || normalizedHost.hostname.endsWith(suffix)) {
      const environment = baseDomain === 'keyond.ca' ? 'production' : 'staging';
      return publicContext(normalizedHost, baseDomain, environment, labels, options);
    }
  }

  return null;
}

export function parseRequestHostContext(
  hostHeader: string | null | undefined,
  protocol: string | null | undefined,
  deploymentBranch?: string,
): HostContext | null {
  const normalizedProtocol = protocol?.replace(/:$/, '');
  return parseHostContext(hostHeader, {
    deploymentBranch,
    ...(normalizedProtocol === 'http' || normalizedProtocol === 'https'
      ? { protocol: normalizedProtocol }
      : {}),
  });
}

export function getCookieOptionsForContext(context: HostContext): HostCookieOptions {
  return {
    ...(context.cookieDomain ? { domain: context.cookieDomain } : {}),
    path: '/',
    sameSite: 'lax',
    secure: context.environment !== 'local',
  };
}

export function getAuthAudienceForContext(context: HostContext | null): AuthAudience {
  return context?.surface === 'account' || context?.surface === 'ops' ? 'owner' : 'consumer';
}

function hostOnlyCookieOptions(context: HostContext): HostCookieOptions {
  const options = getCookieOptionsForContext(context);
  const { domain, ...hostOnlyOptions } = options;
  return hostOnlyOptions;
}

export function getCookieCleanupOptionsForContext(context: HostContext): HostCookieOptions[] {
  const currentOptions = getCookieOptionsForContext(context);
  const options = [currentOptions];
  if (currentOptions.domain)
    options.push(hostOnlyCookieOptions(context));

  if (context.branch && context.baseDomain === 'keyondapp.ca') {
    options.push({
      ...currentOptions,
      domain: `.${ context.branch }.${ context.baseDomain }`,
    });
  }

  return options.filter((option, index, all) =>
    all.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(option)) === index);
}

export function getSupabaseCookieOptionsForContext(
  context: HostContext,
  audience: AuthAudience,
): SupabaseCookieOptions {
  const options = audience === 'owner'
    ? hostOnlyCookieOptions(context)
    : getCookieOptionsForContext(context);
  return {
    ...options,
    name: SUPABASE_STORAGE_KEYS[audience],
  };
}

export function getSupabaseCookieCleanupOptionsForContext(
  context: HostContext,
  audience: AuthAudience,
): SupabaseCookieOptions[] {
  const name = SUPABASE_STORAGE_KEYS[audience];
  return getCookieCleanupOptionsForContext(context).map((options) => ({ ...options, name }));
}

function isSupabaseCookieName(name: string, prefix: string): boolean {
  if (name === prefix)
    return true;

  const suffix = name.slice(prefix.length + 1);
  return (name.startsWith(`${ prefix }-`) || name.startsWith(`${ prefix }.`))
    && /^(?:0|[1-9][0-9]*)$/.test(suffix);
}

export function getSupabaseCookieNames(
  cookieHeader: string,
  supabaseUrl: string | undefined,
  audience?: AuthAudience,
): string[] {
  const names = new Set<string>();

  if (supabaseUrl) {
    let projectRef: string | undefined;
    try {
      projectRef = new URL(supabaseUrl).hostname.split('.')[0];
    } catch {
      projectRef = undefined;
    }

    if (projectRef) {
      const cookiePrefix = `sb-${ projectRef }-auth-token`;
      for (const cookie of cookieHeader.split(';')) {
        const name = cookie.trim().split('=')[0];
        if (isSupabaseCookieName(name, cookiePrefix))
          names.add(name);
      }
    }
  }

  if (audience) {
    const cookiePrefix = SUPABASE_STORAGE_KEYS[audience];
    for (const cookie of cookieHeader.split(';')) {
      const name = cookie.trim().split('=')[0];
      if (isSupabaseCookieName(name, cookiePrefix))
        names.add(name);
    }
  }

  return Array.from(names);
}

export function getHostCookieOptions(
  hostHeader: string | null | undefined,
  options: ParseHostOptions = {},
): HostCookieOptions | undefined {
  const context = parseHostContext(hostHeader, options);
  return context ? getCookieOptionsForContext(context) : undefined;
}

export function buildPortalUrl(
  portalSlug: string,
  context: HostContext,
  mode: PortalUrlMode = 'durable',
): string {
  const normalizedSlug = portalSlug.trim().toLowerCase();
  if (!dnsLabelPattern.test(normalizedSlug))
    throw new Error('Portal slug must be a DNS-safe host label.');

  if (context.environment === 'local') {
    const port = context.port === undefined ? 3000 : context.port;
    return `http://${ normalizedSlug }.localhost:${ port }`;
  }

  if (mode === 'preview-display' && context.environment === 'preview' && context.branch)
    return `https://${ normalizedSlug }.${ context.branch }.keyondapp.ca`;

  if (!context.baseDomain)
    throw new Error('A supported host context is required to build a Portal URL.');

  return `https://${ normalizedSlug }.${ context.baseDomain }`;
}

function configuredCanonicalOrigin(
  environment: string | undefined,
  vercelEnvironment: string | undefined,
): string {
  if (vercelEnvironment === 'preview'
    || environment === 'staging'
    || environment === 'preview')
    return 'https://app.keyondapp.ca';
  if (vercelEnvironment === 'production' || environment === 'production')
    return 'https://app.keyond.ca';
  return 'http://app.localhost:3000';
}

function canonicalEnvironment(env: CanonicalOriginEnvironment): HostEnvironment {
  if (env.VERCEL_ENV === 'preview'
    || env.KEYOND_ENV === 'staging'
    || env.KEYOND_ENV === 'preview')
    return 'staging';
  if (env.VERCEL_ENV === 'production' || env.KEYOND_ENV === 'production')
    return 'production';
  return 'local';
}

export function getCanonicalOriginForEnvironment(env: CanonicalOriginEnvironment = {}): string {
  const configured = env.KEYOND_CANONICAL_ORIGIN?.trim()
    || env.NEXT_PUBLIC_CANONICAL_ORIGIN?.trim();

  if (!configured)
    return configuredCanonicalOrigin(env.KEYOND_ENV, env.VERCEL_ENV);

  let parsed: URL;
  try {
    parsed = new URL(configured);
  } catch {
    throw new Error('KEYOND_CANONICAL_ORIGIN must be a valid HTTP or HTTPS URL.');
  }

  const parsedContext = parseHostContext(parsed.host, {
    deploymentBranch: env.VERCEL_GIT_COMMIT_REF,
    protocol: parsed.protocol === 'http:' ? 'http' : 'https',
  });
  const expectedEnvironment = canonicalEnvironment(env);

  if ((parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
    || parsed.username
    || parsed.password
    || parsed.pathname !== '/'
    || parsed.search
    || parsed.hash
    || !parsedContext
    || parsedContext.environment !== expectedEnvironment
    || parsedContext.branch
    || (expectedEnvironment === 'local'
      ? parsed.protocol !== 'http:'
      : parsed.protocol !== 'https:'))
    throw new Error('KEYOND_CANONICAL_ORIGIN must use an approved Keyond host.');

  return parsed.origin;
}
